import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(true);

  // Langues supportées
  const supportedLanguages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' }
  ];

  // Initialiser la langue depuis URL query, localStorage ou navigateur
  useEffect(() => {
    const initializeLanguage = () => {
      const validLangCodes = supportedLanguages.map(l => l.code);
      
      // 1. Vérifier l'URL query (?langue=nl)
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('langue');
      if (urlLang && validLangCodes.includes(urlLang)) {
        setCurrentLanguage(urlLang);
        localStorage.setItem('preferred_language', urlLang);
        setIsLoading(false);
        return;
      }
      
      // 2. Vérifier localStorage
      const savedLang = localStorage.getItem('preferred_language');
      if (savedLang && validLangCodes.includes(savedLang)) {
        setCurrentLanguage(savedLang);
        setIsLoading(false);
        return;
      }

      // 3. Détecter la langue du navigateur
      const browserLang = navigator.language.split('-')[0]; // 'fr-FR' → 'fr'
      if (validLangCodes.includes(browserLang)) {
        setCurrentLanguage(browserLang);
        localStorage.setItem('preferred_language', browserLang);
      } else {
        // 4. Par défaut : français
        setCurrentLanguage('fr');
        localStorage.setItem('preferred_language', 'fr');
      }
      
      setIsLoading(false);
    };

    initializeLanguage();
  }, []);

  // Changer de langue
  const changeLanguage = (langCode) => {
    if (!supportedLanguages.find(l => l.code === langCode)) {
      console.error(`Langue non supportée: ${langCode}`);
      return;
    }

    setCurrentLanguage(langCode);
    localStorage.setItem('preferred_language', langCode);

    // Construire la nouvelle URL avec le paramètre ?langue=XX
    const url = new URL(window.location.href);
    url.searchParams.set('langue', langCode);
    
    // Rediriger vers la nouvelle URL
    window.location.href = url.toString();
  };

  const value = {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    isLoading,
    // Helper pour obtenir l'info d'une langue
    getLanguageInfo: (code) => supportedLanguages.find(l => l.code === code)
  };

  // Ne plus afficher de spinner - charger directement
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
